import {Component, Inject, OnInit, Optional} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {DialogService, DIALOG_DATA} from 'src/app/services/dialog.service';
import {DialogRef} from '../../dialog/dialog-ref';
import {LinkDocModalComponent} from '../link-doc-modal/link-doc-modal.component';
import {
  columnTypes,
  ITableModalData,
  ITableValidation,
  ProfessionalTableType,
  UITableHeader,
} from '../table.models';
import {Utils} from 'src/app/shared/utils/utils';
import {filter} from 'rxjs';

/**
 * Modal component for adding new row to the table
 */
@UntilDestroy()
@Component({
  selector: 'app-add-table-modal',
  templateUrl: './add-table-modal.component.html',
  styleUrls: ['./add-table-modal.component.scss'],
})
export class AddTableModalComponent implements OnInit {
  public ProfessionalTableType = ProfessionalTableType;
  public isEditable = false; // changes read/edit mode
  public isCreate = false; // shoes add mode
  _data!: any; // data that will be returned from modal
  typeOfData = columnTypes; // enum for the column's type
  /**
   * Reactive form group for the modal
   */
  addModalGroup!: UntypedFormGroup;
  public trainingHeaders = [
    'conferenceName',
    'courseName',
    'presentationName',
    'name',
  ];
  public defaultDropdown: IDropdownObject[] = [];
  public isDatePickerOpened!: boolean;
  public isFieldInvalid = Utils.isFieldInvalid;
  public deadLine: null | Date = null;
  public addictedControlName = '';

  constructor(
    private dialogService: DialogService,
    private dialogRef: DialogRef,
    @Optional() @Inject(DIALOG_DATA) public data: ITableModalData
  ) {}

  ngOnInit(): void {
    this.setTrainingDropdown(this.data.table);
    this._data = {...this.data.content};
    this.addModalGroup = new UntypedFormGroup({});
    this.data.headers.forEach((element: UITableHeader) => {
      this.addModalGroup.addControl(
        element.key,
        new UntypedFormControl(
          null,
          this.data.validation?.find(
            (v: ITableValidation) => v.controlName === element.key
          )?.validators
        )
      );
    });
    if (this.data.table === ProfessionalTableType.TechInterview) {
      this.addModalGroup.controls['employeeId'].setValue(this.data.employeeId);
    }
    this.addModalGroup.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => (this._data = value));

    this.setDateRestrictions(this.data.table);
  }

  /**
   * Method open modall window to attach link
   */
  attachLink(): void {
    this.dialogService
      .open(LinkDocModalComponent, {
        data: null,
        settings: {
          width: '350px',
          height: '250px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(res => !!res)
      )
      .subscribe(result => {
        this.addModalGroup.get('file')?.setValue(result);
      });
  }

  /**
   * This method removes document link from link control
   */
  deleteDocLink(): void {
    this.addModalGroup.controls['file'].setValue('');
  }

  saveData() {
    if (this.addModalGroup.invalid) {
      return Utils.validateAllFormFields(this.addModalGroup);
    }
    Object.keys(this._data).forEach(element => {
      if (this.trainingHeaders.includes(element)) {
        this._data['training'] = this._data[element];
      }
    });
    this._data.link = this.addModalGroup.getRawValue().link;
    this.dialogRef.close(this._data);
  }

  cancel() {
    this.dialogRef.close();
  }

  setTrainingDropdown(tableName: ProfessionalTableType) {
    switch (tableName) {
      case ProfessionalTableType.Conference:
        this.defaultDropdown = this.data.dropdown.Conference;
        break;
      case ProfessionalTableType.InternalCourses:
        this.defaultDropdown = this.data.dropdown.InternalCourses;
        break;
      case ProfessionalTableType.Presentation:
        this.defaultDropdown = this.data.dropdown.Presentation;
        break;
      case ProfessionalTableType.Training:
        this.defaultDropdown = this.data.dropdown.Training;
        break;
      case ProfessionalTableType.TechInterview:
        this.defaultDropdown = this.data.dropdown.InterviewStatus;
        break;
      case ProfessionalTableType.Certifications:
        this.defaultDropdown = this.data.dropdown.EventStatus;
        break;
      case ProfessionalTableType.EnglishCourses:
        this.defaultDropdown = this.data.dropdown.EnglishCourses;
        break;
    }
  }

  setDateRestrictions(tableName: ProfessionalTableType): void {
    let controlName = '';
    switch (tableName) {
      case ProfessionalTableType.Certifications:
        controlName = 'date';
        this.addictedControlName = 'expirationDate';
        break;
      case ProfessionalTableType.AssignedMentors:
        controlName = 'start';
        this.addictedControlName = 'endDate';
        break;
    }
    if (controlName) {
      this.addModalGroup.controls[controlName].valueChanges
        .pipe(
          untilDestroyed(this),
          filter(data => !!data)
        )
        .subscribe(value => {
          this.deadLine = Utils.formatStringToDate(value);
        });
    }
  }
}
